import MoreVertIcon from '@material-ui/icons/MoreVert';
import {
  Box,
  ClickAwayListener,
  Divider,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import EditIcon from 'components/Icons/EditIcon';
import CollapsableCard from 'components/Generic/CollapsableCard';
import { fonts } from 'theme';
import { DialogWidth, SortingCenter, SortingCenterUserRole, User } from 'types';
import DetailsButtonIcon from 'components/Icons/DetailsButtonIcon';
import CollapsableCardHeader from 'components/Generic/CollapsableCardHeader';
import ActionButton from 'components/Buttons/ActionButton';
import { AppContext } from 'context';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { deleteAllSortingCenterUserRolesByAdminSortingCenter } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { forceRefresh } from 'utils/history';
import MoreActionOpenedButtonIcon from 'components/Icons/MoreActionOpenedButtonIcon';
import NewDialog from 'components/NewDialog';

const useStyles = makeStyles(theme => ({
  userActions: {
    width: '100px',
    maxWidth: '100px',
    alignSelf: 'center',
    display: 'flex',
    '& > *:not(:last-child)': {
      marginRight: '3px',
    },
    '& > *:not(:first-child)': {
      marginLeft: '3px',
    },
    '& button': {
      padding: '0.5rem',
      minWidth: 'initial',
    },
    '& a': {
      minWidth: 'initial',
    },
  },
  userFirstName: {
    width: '15%',
    paddingLeft: '10px',
    alignSelf: 'center',
  },
  userLastName: {
    paddingLeft: '20px',
    width: '14%',
    alignSelf: 'center',
  },
  userEmail: {
    paddingLeft: '10px',
    width: '23%',
    alignSelf: 'center',
  },
  userRole: {
    width: '18%',
    alignSelf: 'center',

    '& > span': {
      padding: '5px',
      fontSize: '0.725rem',
      border: '1px solid',
      borderRadius: '10px',
    },
  },
  userSortingCenter: {
    width: '10%',
    alignSelf: 'center',
  },
  userSortingCenterChild: {
    width: '12%',
    alignSelf: 'center',
  },

  userButton: {
    minWidth: '16px',
    minHeight: '22px',
    borderWidth: '2px',
    borderRadius: '4px',
    paddingLeft: '5px',
    paddingRight: '5px',
    margin: '3px',
  },
  userButtonMore: {
    minWidth: '16px',
    minHeight: '32px',
    padding: '2px',
    margin: '3px',
  },

  userCard: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    borderLeft: '0px',
  },
  userTitle: {
    marginLeft: '14px',
    marginRight: '14px',
    fontFamily: fonts.secondary,
    fontWeight: 600,
    borderLeft: '0px',
  },
  userCardContent: {
    padding: '0px',
    paddingTop: '10px',
    paddingBottom: '20px',
    display: 'flex',
    flexDirection: 'row',
    width: '90%',
    borderLeft: '0px',
  },
  textLight: {
    color: '#AAAAAA',
  },

  userCardHeader: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    borderLeft: '0px',

    '& > p': {
      fontSize: '14px',
      fontWeight: '700',
      fontFamily: fonts.secondary,
      lineHeight: '24px',
    },
  },

  divider: {
    margin: '10px 34px 10px 11px',
    height: '60%',
  },
  collapsableDivider: {
    margin: '10px 0 8px 0',
  },

  moreActionButton: {
    width: '0px',
  },
  menuZIndex: {
    zIndex: 1,
  },
  mainPaper: {
    color: "'#757575'",
    display: 'flex',
    marginLeft: '-15px',
    paddingRight: '33px',
  },
  menuList: {
    padding: '0 10px',
  },
  menuItem: {
    padding: '8px',
    height: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      color: '#1aa1e1',
    },
    '&:not(:last-child)': {
      borderBottom: '1px solid',
      borderBottomColor: '#E0E0E0',
    },
  },
  content: {
    marginBottom: '32px',
    marginRight: '32px',

    '& p': {
      fontSize: 16,
      lineHeight: '24px',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& p span': {
      color: '#FF9500',
    },
  },
}));

export const CollapsableUserHeader: React.FC<any> = () => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users' });

  return (
    <CollapsableCardHeader>
      <Box className={classes.userCardHeader}>
        <Typography className={classes.userActions}>{t('action')}</Typography>
        <Typography className={classes.userLastName}>{t('lastName')}</Typography>
        <Typography className={classes.userFirstName}>{t('firstName')}</Typography>
        <Typography className={classes.userEmail}>{t('email')}</Typography>
        <Typography className={classes.userRole}>{t('role')}</Typography>
        <Typography className={classes.userSortingCenter}>{t('sortingCenter')}</Typography>
        <Typography className={classes.userSortingCenterChild}>{t('sortingCenterChild')}</Typography>
      </Box>
    </CollapsableCardHeader>
  );
};

interface CollapsableUserContainerProps {
  user: User;
}

export const CollapsableUserContainer: React.FC<CollapsableUserContainerProps> = ({ user }) => {
  const context = useContext(AppContext);
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users' });
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorRef, setAnchorRef] = useState<any>(null);
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const history = useHistory();

  const handleMenuClick = (event: PointerEvent) => {
    setAnchorRef(event.currentTarget);
    setMenuOpen((previousOpen: boolean) => !previousOpen);
  };

  const handleMenuClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setMenuOpen(false);
  };

  const handleOpenDialogDelete = () => {
    setDialogDeleteOpen(true);
    setMenuOpen(false);
  };

  const handleDialogDelete = async (user: User) => {
    try {
      await deleteAllSortingCenterUserRolesByAdminSortingCenter(transformObjectId(user));
      notify({
        message: t('dialog.delete.success'),
        severity: 'success',
      });
      forceRefresh(history);
    } catch (e) {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
    }
  };

  const sortingCenters = context.getSortingCenters?.();
  const elligibleSortingCenterChild: SortingCenter[] = [];
  sortingCenters?.map((sortingCenter: SortingCenter) => {
    sortingCenter.childrenSortingCenter?.map(sortingCenterChild => {
      elligibleSortingCenterChild.push(sortingCenterChild);
      return null;
    });
    return null;
  });

  const hasMultipleSortingCenter = user.sortingCenterUserRoles.length > 1;

  const collapseContent = user.sortingCenterUserRoles.map((sortingCenterUserRole: SortingCenterUserRole, index) => {
    return (
      <Box key={index} className={classes.userCardContent}>
        <Box className={classes.userActions} />
        <Typography className={`${classes.userLastName} ${classes.textLight}`}>{user.lastName}</Typography>
        <Typography className={`${classes.userFirstName} ${classes.textLight}`}>{user.firstName}</Typography>
        <Typography className={`${classes.userEmail} ${classes.textLight}`}>{user.email}</Typography>
        <Typography className={classes.userRole}>
          <span>{t(`rolesEnum.${sortingCenterUserRole.role}`)}</span>
        </Typography>
        <Typography className={classes.userSortingCenter}>
          {sortingCenterUserRole.sortingCenter.parentSortingCenter?.code}
        </Typography>
        <Typography className={classes.userSortingCenterChild}>{sortingCenterUserRole.sortingCenter.code}</Typography>
      </Box>
    );
  });

  const linkTo = {
    pathname: `/administration/users/${transformObjectId(user)}`,
    state: { referer: location.pathname },
  };

  return (
    <CollapsableCard
      actionClick={menuOpen || dialogDeleteOpen}
      collapsableContent={hasMultipleSortingCenter ? collapseContent : ''}
      disableBorder={true}
      disableCollapse={!hasMultipleSortingCenter}
    >
      <NewDialog
        closable={true}
        isOpen={dialogDeleteOpen}
        onClose={() => setDialogDeleteOpen(false)}
        width={DialogWidth.LARGE_FORM}
        title={t('dialog.delete.title')}
        renderContent={() => (
          <div className={classes.content}>
            <Typography>{t('dialog.delete.content')}</Typography>
          </div>
        )}
        genericFooter={true}
        closeButtonLabelFooter={t('dialog.delete.cancel')}
        continueButtonLabelFooter={t('dialog.delete.continue')}
        handleDialog={() => handleDialogDelete(user)}
      />

      <Popper
        className={classes.menuZIndex}
        anchorEl={anchorRef}
        open={Boolean(menuOpen)}
        role={undefined}
        transition
        placement="right"
        modifiers={{
          preventOverflow: {
            enabled: false,
          },
          hide: {
            enabled: false,
          },
        }}
      >
        <Paper className={classes.mainPaper} elevation={2}>
          <MoreActionOpenedButtonIcon />
          <ClickAwayListener onClickAway={handleMenuClose}>
            <MenuList className={classes.menuList} autoFocusItem={menuOpen} id="menu-list-grow">
              <MenuItem onClick={handleOpenDialogDelete} className={classes.menuItem}>
                {t('delete')}
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
      <Box className={classes.userCard}>
        <Box className={classes.userActions}>
          <ActionButton
            variant="outlined"
            icon={DetailsButtonIcon}
            data-qa-id={'eye-button'}
            className="userAction"
            component={Link}
            to={linkTo}
          />
          <ActionButton
            variant="outlined"
            icon={EditIcon}
            data-qa-id={'edit-button'}
            disabled={true}
            className="userAction"
          />
          <ActionButton
            data-qa-id="action-button"
            icon={() => <MoreVertIcon className={'userAction'} />}
            className={`${classes.moreActionButton} userAction`}
            onClick={(event: PointerEvent) => handleMenuClick(event)}
          />
        </Box>
        <Divider orientation="vertical" flexItem />
        <Typography className={classes.userLastName}>{user.lastName}</Typography>
        <Typography className={classes.userFirstName}>{user.firstName}</Typography>
        <Typography className={classes.userEmail}>{user.email}</Typography>
        <Typography className={classes.userRole}>
          {hasMultipleSortingCenter ? '-' : <span>{t(`rolesEnum.${user.sortingCenterUserRoles[0].role}`)}</span>}
        </Typography>
        <Typography className={classes.userSortingCenter}>
          {hasMultipleSortingCenter ? '-' : user.sortingCenterUserRoles[0].sortingCenter.parentSortingCenter?.code}
        </Typography>
        <Typography className={classes.userSortingCenterChild}>
          {hasMultipleSortingCenter ? '-' : user.sortingCenterUserRoles[0].sortingCenter.code}
        </Typography>
      </Box>
    </CollapsableCard>
  );
};
